import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { TFunction } from "i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import {
  signIn as signInPostRequest,
  SignInErrorType,
} from "@/api/authentication/signIn.ts";
import { login } from "@/api/common/interceptors/authenticationInterceptor.ts";
import { getErrorResponse } from "@/api/common/handleAxiosError.ts";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Trans } from "react-i18next";
import PasswordInput from "@/components/PasswordInput.tsx";
import { setTitle } from "@/hooks/useHeader.ts";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z.object({
    email: z.string().email({ message: t("validation.invalidEmail") }),
    password: z.string().min(1, t("validation.passwordRequired")),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const SignIn = ({ className, ...props }: ComponentProps<"div">) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    setTitle(t("auth.signIn.title"));
  }, [t]);

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setError("");
    try {
      const response = await signInPostRequest(values);
      await login({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
      });
      navigate("/");
    } catch (e: unknown) {
      const response = getErrorResponse<SignInErrorType>(e);
      setError(response?.detail);
    }
    setLoading(false);
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.signIn.heading")}</CardTitle>
          <CardDescription>{t("auth.signIn.description")}</CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("auth.signIn.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-6">
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("form.email")}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t("form.emailPlaceholder")}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center">
                          <FormLabel htmlFor="password">
                            {t("form.password")}
                          </FormLabel>
                          <Link
                            to="/forgot-password"
                            className="ml-auto text-sm underline-offset-4 hover:underline"
                          >
                            {t("auth.signIn.forgotPassword")}
                          </Link>
                        </div>
                        <FormControl>
                          <PasswordInput
                            id="password"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={loading}>
                    {t("auth.signIn.submit")}{" "}
                    {loading && <LoaderCircle className="animate-spin" />}
                  </Button>
                </div>
                <div className="text-center text-sm">
                  {t("auth.signIn.noAccount")}{" "}
                  <Link to="/sign-up" className="underline underline-offset-4">
                    {t("auth.signIn.signUpLink")}
                  </Link>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <div className="text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
        <Trans
          i18nKey="auth.signIn.agreement"
          components={{
            terms: <Link to="/terms-of-service" />,
            privacy: <Link to="/privacy-policy" />,
          }}
        />
      </div>
    </div>
  );
};

export default SignIn;
